import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { withFormik } from 'formik';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose, defaultTo, path } from 'ramda';
import Select from 'react-select';
import moment from 'moment';
import * as yup from 'yup';

import magic from '../../../../util/magic';
import { isNilOrEmpty } from '../../../../util/redux';
import { handleError, handleSuccess } from '../../../../util/forms';
import { apiAddGroupFile } from '../../../../api/ceu';
import { apiGetPreapprovedCreditDiscount } from '../../../../api/discount';

import Card from '../../../../components/Card';
import FlaggedUserModal from '../../../../components/Modal/FlaggedUserModal';
import DiscountForm from '../../../../components/DiscountForm';
import StripeCheckout from '../../../../components/StripeCheckout';
import Checkbox from '../../../../components/Forms/Checkbox';
import EditField from '../../../../components/Forms/EditField';
import Radio from '../../../../components/Forms/Radio';
import FileBrowser from '../../../../components/Forms/FileBrowser';

const mapStore = ({ creditTypes, preapprovedCreditFee }) => ({
  creditTypes,
  fee: preapprovedCreditFee,
});

class CEU extends Component {
  static propTypes = {
    approve: PropTypes.func,
    reject: PropTypes.func,
    ceu: PropTypes.object,
    creditTypes: PropTypes.array,
    finish: PropTypes.func,
    history: PropTypes.object,
    submit: PropTypes.func,
    fee: PropTypes.object,
  };

  static defaultProps = {
    ceu: {},
  };

  constructor(props) {
    super(props);

    this.state = {
      price: props.fee.price,
      discount: {},
    };
  }

  render() {
    const {
      approve,
      reject,
      ceu,
      creditTypes,
      values,
      handleBlur,
      handleChange,
      handleSubmit,
      isSubmitting,
      isValid,
      setFieldValue,
      setValues,
      status,
      fee,
      errors,
    } = this.props;
    const reviews = defaultTo([], ceu.reviews);
    const firstName = path(['user', 'firstName'], ceu);
    const lastName = path(['user', 'lastName'], ceu);

    return (
      <div>
        {approve && reject && (
          <FlaggedUserModal
            user={ceu.user}
          >
          </FlaggedUserModal>
        )}
        <Card className="container">
          <Card.Header compact>Continuing Education (CEU) Pre-Approval Form</Card.Header>
          <Card.Body>
            <div className="row">
              <div className="offset-md-1 col-md-10 pl-0 mb-4">
                <p>
                  Submitted programs must pertain to
                  <strong>
                    <em>{' personal finance '}</em>
                  </strong>
                  subject matter or counseling/coaching skills, must enhance Sage Financial Solutions certified professionals’ ability and
                  expertise, and must meet the requirements of Sage Financial Solutions certification.
                </p>
                <h6>The Approval Process:</h6>
                <ul>
                  <li>Submit the CEU Pre-Approval Form completing all required fields.</li>
                  <li>
                    Sage Financial Solutions®️ will review the request, modify if necessary, and either approve or reject your submission.
                    You will receive an email from <a href="mailto:ceu@Sage Financial Solutions.org">ceu@Sage Financial Solutions.org</a> with the final
                    determination.
                  </li>
                  <li>
                    Following the conference or workshop, each individual certificant must submit an Individual CEU form.
                  </li>
                </ul>
                <p>
                  NOTE: If you are submitting your conference or workshop for pre-approval for Sage Financial Solutions certified
                  professionals to earn CEUs for attendance, please include your preferred code phrase on the form.
                  Individuals who participate in your program will be required to submit the code phrase as proof of
                  participation.
                </p>
              </div>
            </div>
            <form className="offset-md-1">
              {firstName && (
                <div className="form-group row">
                  <label htmlFor={name} className="col-form-label col-md-3">
                    Name
                  </label>
                  <div className="col-md-7 d-flex align-items-center">
                    {firstName} {lastName}
                  </div>
                </div>
              )}
              <EditField
                type="number"
                name="credits"
                label="CEUs Requested"
                required
                status={status}
                onChange={handleChange}
                error={errors.credits}
                onBlur={handleBlur}
                value={values.credits}
              />
              <EditField
                as={Select}
                bare
                simpleValue
                name="creditTypeId"
                label="Credit Type"
                onChange={(value) => setFieldValue('creditTypeId', value)}
                options={creditTypes.map((type) => ({ value: type.id, label: type.name }))}
                value={values.creditTypeId}
              />
              <EditField
                name="programTitle"
                label="Program Title"
                required
                status={status}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.programTitle}
              />
              <EditField
                name="sponsor"
                label="Sponsor"
                status={status}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.sponsor}
              />
              <EditField
                as="textarea"
                name="programDescription"
                label="Program Description"
                status={status}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.programDescription}
              />
              <div className="form-group row mb-0">
                <label className="col-md-3 col-form-label">Program Agenda</label>
                <div className="col-md-7">
                  <Radio
                    inline
                    name="agendaMethod"
                    label="Upload Agenda"
                    value="upload"
                    checked={values.agendaMethod}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                  <Radio
                    inline
                    name="agendaMethod"
                    label="Type into text box"
                    value="type"
                    checked={values.agendaMethod}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                </div>
              </div>
              {values.agendaMethod == 'upload' && (
                <div className="row form-group">
                  <div className="offset-md-3 col-md-7">
                    <FileBrowser
                      files={values.media}
                      fileKey={magic.programAgenda}
                      name="programAgendaFile"
                      addFile={(file) => apiAddGroupFile(ceu.id, file)}
                      update={(media) => setFieldValue('media', media)}
                      fake={isNilOrEmpty(ceu.id)}
                      fakeFiles={values.fakeMedia}
                      fakeUpdate={({ files, fakeFiles }) => setValues({ ...values, media: files, fakeMedia: fakeFiles })}
                    />
                  </div>
                </div>
              )}
              {values.agendaMethod == 'type' && (
                <EditField
                  as="textarea"
                  label="&nbsp;"
                  name="programAgenda"
                  status={status}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.programAgenda}
                />
              )}
              <EditField
                name="completionCode"
                label="Completion Code"
                status={status}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.completionCode}
                tip="Required if you are submitting your conference or workshop for pre-approval for Sage Financial Solutions certified
                professionals to earn CEUs for attendance. Individuals who participate in your program will be required to
                submit this code phrase as proof of participation. If you are submitting an individual request for
                approval, leave blank."
              />
              <EditField
                name="startDate"
                label="Start Date"
                type="date"
                status={status}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.startDate}
              />
              <EditField
                name="endDate"
                label="End Date"
                type="date"
                status={status}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.endDate}
              />
              <Checkbox
                name="listProgram"
                label="Please List my program on the Sage Financial Solutions CEU library page and include in communications."
                tip="($30 members / $40 non-members)"
                checked={values.listProgram}
                onBlur={handleBlur}
                onChange={handleChange}
              />
              {reviews.length > 0 && (
                <div className="row">
                  <div className="col-md-10">
                    <h4 className="bold-blue-heading mt-5 mb-3">CEU Reviews</h4>
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Reviewer</th>
                          <th>Message</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reviews.map((review) => (
                          <tr key={review.id}>
                            <td>{moment(review.createdAt).format('MM/DD/YYYY')}</td>
                            <td>
                              {path(['user', 'firstName'], review)} {path(['user', 'lastName'], review)}
                            </td>
                            <td>{review.message}</td>
                            <td>{path(['extra', 'status'], review)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {values.listProgram && !ceu.id && (
                <div className="col-md-10">
                  <DiscountForm
                    discount={values.discount}
                    checkDiscount={(discountCode) => apiGetPreapprovedCreditDiscount({ discountCode })}
                    update={(data) => {
                      setFieldValue('price', data.payment / 100, false);
                      setFieldValue('discount', defaultTo({}, data.discount), false);
                    }}
                  />
                </div>
              )}
              <div className="form-group row row mb-0">
                <div className="col-md-10 text-right">
                  {reject && (
                    <button type="button" className="btn btn-outline-danger mr-2" onClick={reject}>
                      Reject
                    </button>
                  )}
                  {approve && (
                    <button type="button" className="btn btn-secondary mr-2" onClick={approve}>
                      Approve
                    </button>
                  )}
                  {values.listProgram && !ceu.id ? (
                    <StripeCheckout
                      name="Sage Financial Solutions"
                      description="Group CEU"
                      amount={values.price}
                      pay={(token, overrides = {}) => {
                        Promise.resolve(setFieldValue('token', token, false))
                          .then(() => setFieldValue('overrides', overrides, false))
                          .then(handleSubmit);
                      }}
                    />
                  ) : (
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={!isValid || isSubmitting}
                      onClick={handleSubmit}
                    >
                      {ceu.id ? 'Save Changes' : 'Request'}
                    </button>
                  )}
                </div>
              </div>
            </form>
          </Card.Body>
        </Card>
      </div>
    );
  }
}

export default compose(
  withRouter,
  connect(mapStore),
  withFormik({
    handleSubmit: (payload, formProps) => {
      const { props } = formProps;

      props
        .submit({ discountCode: payload.discount.code, ...payload })
        .then((resp) => {
          handleSuccess(formProps);
          return resp;
        })
        .then((resp) => props.finish(resp))
        .catch((err) => handleError(formProps, err));
    },
    mapPropsToValues: ({ ceu = {}, fee = {} }) => {
      return {
        id: ceu.id,
        credits: defaultTo('', ceu.credits),
        creditTypeId: defaultTo('', ceu.creditTypeId),
        programTitle: defaultTo('', ceu.programTitle),
        sponsor: defaultTo('', ceu.sponsor),
        programDescription: defaultTo('', ceu.programDescription),
        agendaMethod: defaultTo('upload', ceu.programAgenda ? 'type' : 'upload'),
        programAgenda: defaultTo('', ceu.programAgenda),
        completionCode: defaultTo('', ceu.completionCode),
        listProgram: ceu.listProgram,
        startDate: defaultTo(moment().format('YYYY-MM-DD'), ceu.startDate),
        endDate: defaultTo(moment().format('YYYY-MM-DD'), ceu.endDate),
        media: defaultTo([], ceu.media),
        fakeMedia: [],
        price: fee.price,
        discount: {},
        overrides: {},
      };
    },
    validationSchema: () =>
      yup.object().shape({
        credits: yup.number().required('This field is required'),
      }),
  }),
  connect(mapStore),
)(CEU);
